const REPLY_MARKER = /^\[\[reply-to-comment:(\d+)\]\]$/m

export interface ThreadedComment {
    comment: any
    parent: any | null
    depth: number
    rootCreatedAt: string
    threadOrder: number
}

export const getCommentParentId = (comment: any): number | null => {
    const explicit = comment?.parent_comment_id
        ?? comment?.parent_id
        ?? comment?.reply_to_comment_id
        ?? comment?.reply_to?.id
    const marker = String(comment?.content || '').match(REPLY_MARKER)?.[1]
    const value = Number(explicit ?? marker)
    return Number.isFinite(value) && value > 0 ? value : null
}

export const stripCommentReplyMarker = (content: string): string => String(content || '')
    .split('\n')
    .filter((line) => !REPLY_MARKER.test(line.trim()))
    .join('\n')
    .trim()

export const addCommentReplyMarker = (content: string, parentId?: number | null): string => {
    const cleanContent = stripCommentReplyMarker(content)
    return parentId ? `${cleanContent}\n[[reply-to-comment:${parentId}]]` : cleanContent
}

const createdAtTime = (comment: any) => new Date(comment?.created_at).getTime() || 0

export const buildCommentThread = (comments: any[]): ThreadedComment[] => {
    const sorted = [...comments].sort((a, b) => createdAtTime(a) - createdAtTime(b))
    const byId = new Map(sorted.map((comment) => [Number(comment.id), comment]))
    const children = new Map<number, any[]>()
    const roots: any[] = []

    sorted.forEach((comment) => {
        const parentId = getCommentParentId(comment)
        if (parentId && parentId !== Number(comment.id) && byId.has(parentId)) {
            children.set(parentId, [...(children.get(parentId) || []), comment])
        } else {
            roots.push(comment)
        }
    })

    const result: ThreadedComment[] = []
    const visited = new Set<number>()
    let order = 0

    const visit = (comment: any, parent: any | null, depth: number, rootCreatedAt: string) => {
        const id = Number(comment.id)
        if (visited.has(id)) return
        visited.add(id)
        result.push({ comment, parent, depth, rootCreatedAt, threadOrder: order++ })
        ;(children.get(id) || []).forEach((child) => visit(child, comment, depth + 1, rootCreatedAt))
    }

    roots.forEach((root) => visit(root, null, 0, root.created_at))
    sorted.forEach((comment) => {
        if (!visited.has(Number(comment.id))) visit(comment, null, 0, comment.created_at)
    })

    return result
}

export interface CommentThreadRails {
    hasNextSibling: boolean
    continuationDepths: number[]
}

/**
 * Connector lines for a rendered thread: whether a comment is followed by a
 * sibling at its own level, and which ancestor levels still need a vertical
 * line drawn through this row.
 */
export const buildCommentThreadRails = (items: Array<{ depth: number; data: any }>): CommentThreadRails[] => {
    const nextSiblingFlags = items.map((item, index) => {
        const parentId = getCommentParentId(item.data)

        for (let next = index + 1; next < items.length; next += 1) {
            if (items[next].depth < item.depth) break
            if (items[next].depth === item.depth && getCommentParentId(items[next].data) === parentId) {
                return true
            }
        }

        return false
    })

    return items.map((item, index) => {
        const ancestorIndexes: number[] = []

        for (let previous = 0; previous < index; previous += 1) {
            const previousDepth = items[previous].depth
            ancestorIndexes[previousDepth] = previous
            ancestorIndexes.length = previousDepth + 1
        }

        return {
            hasNextSibling: nextSiblingFlags[index],
            continuationDepths: ancestorIndexes.reduce<number[]>((depths, ancestorIndex, depth) => {
                if (depth > 0 && depth < item.depth && nextSiblingFlags[ancestorIndex]) {
                    depths.push(depth)
                }
                return depths
            }, []),
        }
    })
}
